import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { map, tap } from "rxjs/operators";
import {
  PatientPlanService,
  PatientPlanFavouriteSaveRequest,
  PatientPlanDetailResponse,
  CardModel,
  CardSessionCardDto,
  CardSessionDto,
  PatientPlanDto,
  PatientPlanFavouriteSaveRequestType,
  ReusePlanRequest,
} from "../../../swagger/api.client.generated";
import { DateHelper } from "../../core/helpers/date.helper";
import { HomeService } from "../home/home.service";
import { HandLookupService } from "../hand/hand-lookup.service";

@Injectable({
  providedIn: "root",
})
export class PlanService {
  /** The last answer for today's hand, with the hand day it was for; a new app day makes it stale. */
  private todayHand: { date: string; exists: boolean } | null = null;

  /** Fires when a refresh has changed whether today's hand exists, so a widget can redraw. */
  readonly todayHandChanged = new Subject<void>();

  constructor(
    private patientPlanService: PatientPlanService,
    private homeService: HomeService,
    private handLookup: HandLookupService
  ) {}

  /**
   * Asks the API whether the current app day has a hand (FLIP-606) and remembers the answer.
   *
   * A failed call leaves the previous answer alone: an error must not read as "no hand", or the landing page
   * would offer to build one that already exists. "Today" is DateHelper.getCurrentHandDate(), the same app day
   * the hand page, Home and the plan check use.
   */
  refreshTodayHand(): Observable<boolean> {
    const date = DateHelper.getCurrentHandDate();
    return this.handLookup.forDate(date).pipe(
      tap((result) => {
        if (result.state === "error") return;
        const exists = result.state === "hand";
        const changed = this.hasHandToday() !== exists;
        this.todayHand = { date, exists };
        if (changed) this.todayHandChanged.next();
      }),
      map(() => this.hasHandToday())
    );
  }

  /** Whether the last refresh found a hand for the current app day. False until one has run for today. */
  hasHandToday(): boolean {
    return this.todayHand?.date === DateHelper.getCurrentHandDate() && this.todayHand.exists;
  }

  /** The landing page's "today is planned" check: a hand for the app day, or an old plan on it. */
  hasHandOrPlanToday(): boolean {
    return this.hasHandToday() || this.getTodayPlanId() !== null;
  }

  savePatientPlanFavouriteApi(saveRequest: PatientPlanFavouriteSaveRequest) {
    return this.patientPlanService.savePatientPlanFavourite(saveRequest);
  }

  reusePlan(request: ReusePlanRequest) {
    return this.patientPlanService.reusePlan(request);
  }

  /**
   * Whether a plan already exists for the day the user has picked.
   *
   * The picked day is taken at face value — the user chose a date on a calendar, so this is a
   * calendar-date question, not an app-day one. Contrast getTodayPlanId(), where "today" has to
   * be resolved through the app day first.
   */
  checkIfPlanExistsOnSameDate(startDate: Date): boolean {
    return this.findPlanOnDay(startDate) !== null;
  }

  /**
   * The id of the plan for the current APP day, or null.
   *
   * Resolves "today" through DateHelper.getCurrentAppDay() rather than the calendar date, so
   * between midnight and the 3:59am boundary this still means yesterday's plan — the same
   * "today" the plan widgets and the score surfaces use. Before FLIP-218 this used a plain
   * midnight-to-midnight window and disagreed with those widgets during exactly that window.
   */
  getTodayPlanId(): number | null {
    const currentPlan = this.homeService.getCurrentPlan;
    if (currentPlan && currentPlan.patientPlanId) {
      return currentPlan.patientPlanId;
    }
    return this.findPlanOnDay(DateHelper.getCurrentAppDay());
  }

  /**
   * The id of the plan whose start date is `day`, or null.
   *
   * Matches on date components via DateHelper.isPlanOnDay rather than on a datetime range. A
   * plan's startDate is a day marker written into the UTC components by
   * DateHelper.buildPlanStartDate, so reading the same components back is the exact inverse and
   * needs no boundary arithmetic at all — which is what let the midnight-to-midnight range
   * workaround go (FLIP-218).
   */
  private findPlanOnDay(day: Date): number | null {
    const patientPlans: PatientPlanDto[] = this.homeService.getAllPatientPlans;
    const planFound = patientPlans.find((p: PatientPlanDto) =>
      DateHelper.isPlanOnDay(p.startDate, day),
    );
    return planFound?.patientPlanId ?? null;
  }

  checkIfPlanExistsToday(): boolean {
    return this.getTodayPlanId() !== null;
  }

  formatToSaveFavourite(
    patientPlan: PatientPlanDetailResponse,
    planName: string
  ): PatientPlanFavouriteSaveRequest {
    // startDate is the UTC day-marker convention (DateHelper.buildPlanStartDate), matched by
    // isPlanOnDay()/buildDraftDateKey() on UTC date components - not the raw local instant
    // getTodayLocal() produces. insertLocalDate stays local; it's a separate, genuinely
    // wall-clock field.
    const startDate = DateHelper.buildPlanStartDate(DateHelper.getCurrentAppDay());
    const insertLocalDate = DateHelper.getTodayLocal();
    return new PatientPlanFavouriteSaveRequest({
      patientPlanId: patientPlan.patientPlanId,
      patientId: 0,
      planName: planName,
      type: PatientPlanFavouriteSaveRequestType.New,
      patientPlan: new PatientPlanDto({
        patientPlanId: patientPlan.patientPlanId,
        startDate: startDate,
        insertLocalDate: insertLocalDate,
        insertedByUserId: 0,
        updatedByUserId: 0,
        cardSessions:
          patientPlan.planDailyPeriods?.reduce(
            (acc, period) =>
              acc.concat(
                period.cardSessions?.map((session) => {
                  const allCards: CardModel[] = [];
                  if (session.cardSections?.angelCardsBefore) {
                    allCards.push(...session.cardSections.angelCardsBefore);
                  }
                  if (session.cardSections?.activityCard) {
                    allCards.push(...session.cardSections.activityCard);
                  }
                  if (session.cardSections?.angelCards) {
                    allCards.push(...session.cardSections.angelCards);
                  }
                  const cardSessionCards = allCards.map(
                    (card, index) =>
                      new CardSessionCardDto({
                        cardSessionCardId: card.cardSessionCardId,
                        cardSessionId: session.cardSessionId,
                        cardId: card.cardId,
                        cardName: card.cardName,
                        cardDescription: card.cardDescription,
                        periodMinutes: card.periodMinutes || 0,
                        sortOrder: card.sortOrder || index,
                        isComplete: card.isComplete || false,
                        isActive: true,
                        cardCategoryId: card.cardCategory?.cardCategoryId,
                        cardCategoryName: card.cardCategory?.cardCategoryName,
                        cardDeckId: card.cardDeckId,
                        cardDeckName: card.cardDeckType?.cardDeckName,
                        htmlScript: card.htmlScript,
                        isMandatory: card.isMandatory || false,
                      })
                  );
                  return new CardSessionDto({
                    cardSessionId: session.cardSessionId,
                    patientPlanId: patientPlan.patientPlanId,
                    dailyPeriodId: period.dailyPeriodId,
                    dailyPeriodName: period.dailyPeriod,
                    sortOrder: session.sessionSortOrder || 0,
                    isActive: true,
                    cardSessionCards: cardSessionCards,
                  });
                }) || []
              ),
            []
          ) || [],
      }),
    });
  }
}
